if (!customElements.get('search-suggestions')) {
  const SearchSuggestions = class extends HTMLElement {
    connectedCallback() {
      this.searchTimeoutID = -1;
      this.searchInput = this.closest('form').querySelector('input[name="q"]');
      this.searchInput.addEventListener('keyup', this.handleInputChange.bind(this));
      this.searchInput.addEventListener('change', this.handleInputChange.bind(this));
      this.searchInput.addEventListener('focus', this.handleInputChange.bind(this));
      
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target) && !this.searchInput.contains(e.target)) {
          this.innerHTML = '';
        }
      });
      
      this.searchInput.addEventListener('keydown', this.handleKeyDown.bind(this));
    }

    handleKeyDown(e) {
      const suggestions = this.querySelectorAll('.search-suggestion__link');
      if (!suggestions.length) return;
      
      const currentIndex = Array.from(suggestions).findIndex(el => el === document.activeElement);
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (currentIndex < suggestions.length - 1) {
          suggestions[currentIndex + 1].focus();
        } else {
          suggestions[0].focus();
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentIndex > 0) {
          suggestions[currentIndex - 1].focus();
        } else {
          this.searchInput.focus();
        }
      } else if (e.key === 'Escape') {
        this.innerHTML = '';
        this.searchInput.blur();
      }
    }

    handleInputChange() {
      const valueToSearch = this.searchInput.value.trim();

      if (valueToSearch.length >= 2 && valueToSearch !== this.oldSearchValue) {
        clearTimeout(this.searchTimeoutID);
        this.searchTimeoutID = setTimeout(() => {
          this.fetchSuggestions(valueToSearch);
        }, 300);
      } else if (valueToSearch.length < 2) {
        clearTimeout(this.searchTimeoutID);
        if (this.searchSuggestionsAbortController) {
          this.searchSuggestionsAbortController.abort();
          this.searchSuggestionsAbortController = null;
        }
        this.innerHTML = '';
      }
      this.oldSearchValue = valueToSearch;
    }

    async fetchSuggestions(terms) {
  if (this.searchSuggestionsAbortController) {
    this.searchSuggestionsAbortController.abort();
  }
  this.searchSuggestionsAbortController = new AbortController();

  try {
    // Get suggestions (max 10)
    const suggestUrl = `/search/suggest.json?q=${encodeURIComponent(terms)}&resources[type]=product&resources[limit]=10`;
    const suggestResponse = await fetch(suggestUrl, { signal: this.searchSuggestionsAbortController.signal });
    
    if (!suggestResponse.ok) throw new Error(suggestResponse.status);
    
    const suggestData = await suggestResponse.json();
    
    // Make a second call to get total count from the actual search page
    const countUrl = `/search?q=${encodeURIComponent(terms)}&type=product&view=count`;
    
    // Render suggestions immediately
    this.renderSuggestions(suggestData, terms);
    
    // Then fetch and update the count
    fetch(countUrl)
      .then(r => r.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const countText = doc.querySelector('.utility-bar__centre')?.textContent;
        const match = countText?.match(/(\d+)/);
        if (match) {
          const totalCount = parseInt(match[1]);
          const viewAllLink = this.querySelector('.search-suggestion__view-all');
          if (viewAllLink) {
            viewAllLink.textContent = `View all ${totalCount} result${totalCount !== 1 ? 's' : ''}`;
          }
        }
      })
      .catch(() => {}); // Silently fail if count fetch doesn't work
    
  } catch (error) {
    if (error.name === 'AbortError') return;
    console.error('Search error:', error);
  }
}

renderSuggestions(data, terms) {
  const products = data.resources.results.products || [];
  
  // Get total count from API response
  const totalCount = data.resources.results.queries?.[0]?.total_results || 
                     data.resources.results.total_results ||
                     products.length;
  
  console.log('Total results available:', totalCount); // Debug log
  
  if (products.length === 0) {
    this.innerHTML = '';
    return;
  }

  let html = '<div class="search-suggestions"><div class="search-suggestions__products">';
  
  products.forEach(product => {
    const image = product.image || product.featured_image;
    const imageUrl = image ? this.getImageUrl(image, 120) : '';
    
    // Prices are already in dollars from the API, not cents
    let minPrice = product.price_min || product.price || 0;
    let maxPrice = product.price_max || product.price || 0;
    let comparePrice = product.compare_at_price_max || product.compare_at_price || 0;
    
    // Ensure prices are numbers
    minPrice = parseFloat(minPrice) || 0;
    maxPrice = parseFloat(maxPrice) || 0;
    comparePrice = parseFloat(comparePrice) || 0;
    
    const priceVaries = minPrice !== maxPrice && maxPrice > 0;
    const onSale = comparePrice > maxPrice;
    
    html += `
      <a href="${product.url}" class="search-suggestion__link">
        <div class="search-suggestion__image">
          ${imageUrl ? 
            `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" width="60" height="60" loading="lazy">` : 
            `<div class="search-suggestion__no-image">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </div>`
          }
        </div>
        <div class="search-suggestion__details">
          <div class="search-suggestion__title">${this.escapeHtml(product.title)}</div>
          ${product.vendor ? `<div class="search-suggestion__vendor">${this.escapeHtml(product.vendor)}</div>` : ''}
          <div class="search-suggestion__price">
            ${onSale ? `<span class="search-suggestion__compare-price">$${comparePrice.toFixed(2)}</span>` : ''}
            <span class="search-suggestion__current-price">
              ${priceVaries ? `From $${minPrice.toFixed(2)}` : `$${minPrice.toFixed(2)}`}
            </span>
          </div>
        </div>
      </a>
    `;
  });
  
  html += `</div>
  <a href="/search?q=${encodeURIComponent(terms)}" class="search-suggestion__view-all">
    View all results (${products.length}+ available)
  </a>
</div>`;
  
  this.innerHTML = html;
}

    getImageUrl(image, size) {
      if (typeof image === 'string') {
        return image.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}x.$1`);
      }
      return image;
    }

    formatMoney(cents) {
      return `$${(cents / 100).toFixed(2)}`;
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  };

  window.customElements.define('search-suggestions', SearchSuggestions);
}
